/**
 * ACLCell Component
 * 
 * Renders Parse ACL values with:
 * - Summary display: "Public R+W", "Role: admin", etc.
 * - Click to expand for full permission breakdown
 * - Different colors for public vs restricted
 */

import { CellRendererProps, ParseACL } from '../../../../../types/AppPanel';

export default function ACLCell({ 
  value, 
  onExpand 
}: CellRendererProps<ParseACL | null | undefined>) {
  // Handle null/undefined
  if (value === null) {
    return <span className="text-white/30 italic">(null)</span>;
  }
  if (value === undefined) {
    // Default ACL is public read+write
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full 
                       bg-emerald-500/20 text-emerald-400 text-xs font-medium">
        Public R+W
      </span>
    );
  }

  // Parse ACL and build summary
  const summary = parseACLSummary(value);

  // Determine color based on permissions
  const isPublic = summary.includes('Public');
  const isMasterKeyOnly = summary === 'Master Key Only';

  let colorClass = 'bg-white/10 text-white/70';
  if (isPublic) {
    colorClass = 'bg-emerald-500/20 text-emerald-400';
  } else if (isMasterKeyOnly) {
    colorClass = 'bg-red-500/20 text-red-400';
  } else {
    colorClass = 'bg-amber-500/20 text-amber-400';
  }

  return (
    <button
      onClick={onExpand}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full 
                  text-xs font-medium hover:opacity-80 transition-opacity cursor-pointer
                  ${colorClass}`}
      title="Click to view full ACL"
    >
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
      {summary}
    </button>
  );
}

/**
 * Parse ACL object and return human-readable summary
 */
function parseACLSummary(acl: ParseACL): string {
  const keys = Object.keys(acl);
  
  if (keys.length === 0) {
    return 'Master Key Only';
  }

  const pieces: string[] = [];

  // Check for public permissions
  if (acl['*']) {
    const pub = acl['*'];
    if (pub.read && pub.write) {
      pieces.push('Public R+W');
    } else if (pub.read) {
      pieces.push('Public Read');
    } else if (pub.write) {
      pieces.push('Public Write');
    }
  }

  // Count roles and users
  let roleCount = 0;
  let userCount = 0;

  for (const key of keys) {
    if (key === '*') continue;
    if (key.startsWith('role:')) {
      roleCount++;
    } else {
      userCount++;
    }
  }

  if (roleCount > 0) {
    pieces.push(`${roleCount} role${roleCount > 1 ? 's' : ''}`);
  }
  if (userCount > 0) {
    pieces.push(`${userCount} user${userCount > 1 ? 's' : ''}`);
  }

  if (pieces.length === 0) {
    return 'Master Key Only';
  }

  // Truncate if too long
  const summary = pieces.join(', ');
  return summary.length > 25 ? summary.slice(0, 22) + '...' : summary;
}

